import React, { useEffect, useState, useMemo } from 'react'
import withAuth from '../withAuth'
import Breadcrumbs from '../../components/Common/Breadcrumb'
import { Card, CardBody, Col, Container, Row, Spinner, Button, Input } from 'reactstrap'
import axios from 'axios'
import { baseUrl } from '../../Network'
import { handleAuthError, openExternalLaunchUrl } from '../../utils'
import { isMarketerReadOnly } from '../../helpers/adminRole'
import { toast } from 'react-toastify'
import {
  Users,
  UserCheck,
  Baby,
  BookOpen,
  GraduationCap,
  Package,
  Building2,
  TrendingUp,
  Activity,
  AlertCircle,
  UserPlus
} from 'lucide-react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const primary = '#88167a'
const primaryLight = 'rgba(136, 22, 122, 0.12)'
const successColor = '#10b981'
const successLight = 'rgba(16, 185, 129, 0.15)'
const warnColor = '#f59e0b'
const warnLight = 'rgba(245, 158, 11, 0.15)'

const StatCard = ({ icon: Icon, title, value, subtitle, accent = primary, bgLight = primaryLight }) => (
  <Card className="border-0 shadow-sm rounded-3 overflow-hidden" style={{ borderLeft: `4px solid ${accent}`, background: '#fff' }}>
    <CardBody className="d-flex align-items-start">
      <div className="rounded-3 d-flex align-items-center justify-content-center me-3" style={{ width: 48, height: 48, background: bgLight }}>
        <Icon size={24} style={{ color: accent }} />
      </div>
      <div className="flex-grow-1 min-w-0">
        <div className="text-muted small text-uppercase tracking-wide" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>{title}</div>
        <div className="fw-bold fs-4 mt-1" style={{ color: '#1e293b' }}>{value}</div>
        {subtitle != null && <div className="text-muted small mt-1">{subtitle}</div>}
      </div>
    </CardBody>
  </Card>
)

const MetricBar = ({ label, value, total, color = primary, showPct = true }) => {
  const pct = total ? Math.min(100, Math.round((value / total) * 100)) : 0
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="small text-muted">{label}</span>
        {showPct && <span className="small fw-semibold" style={{ color }}>{pct}%</span>}
      </div>
      <div className="rounded-pill overflow-hidden" style={{ height: 8, background: 'rgba(0,0,0,0.06)' }}>
        <div className="h-100 rounded-pill" style={{ width: `${pct}%`, background: color, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  )
}

const PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 }
]

const Dashboard = () => {
  const readOnly = isMarketerReadOnly()
  const [summary, setSummary] = useState({
    totalParents: 0,
    activeParents: 0,
    totalChildren: 0,
    totalPrograms: 0,
    teacherAssignedCount: 0,
    enrolledCount: 0,
    totalPackages: 0,
    totalPartners: 0,
    registrationsByDate: []
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateFilter, setDateFilter] = useState({ type: 'days', days: 30 })
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [exploringCbt, setExploringCbt] = useState(false)
  const [exploringAi, setExploringAi] = useState(false)
  const [exploringPractice, setExploringPractice] = useState(false)

  const derived = useMemo(() => {
    const {
      totalParents,
      activeParents,
      totalChildren,
      totalPrograms,
      teacherAssignedCount,
      enrolledCount
    } = summary
    const programsWithoutTeacher = Math.max(0, (totalPrograms ?? 0) - (teacherAssignedCount ?? 0))
    const activeParentRatePct = totalParents ? Math.min(100, Math.round((activeParents / totalParents) * 100)) : 0
    const assignmentRatePct = totalPrograms ? Math.min(100, Math.round((teacherAssignedCount / totalPrograms) * 100)) : 0
    const enrollmentRatePct = totalPrograms ? Math.min(100, Math.round((enrolledCount / totalPrograms) * 100)) : 0
    const childrenPerParent = totalParents ? ((totalChildren ?? 0) / totalParents).toFixed(1) : '0'
    return {
      programsWithoutTeacher,
      activeParentRatePct,
      assignmentRatePct,
      enrollmentRatePct,
      childrenPerParent
    }
  }, [summary])

  const registrationChartData = useMemo(() => {
    const list = summary.registrationsByDate || []
    const labels = list.map(({ date }) => {
      if (!date) return ''
      const str = typeof date === 'string' ? date : (date.toISOString ? date.toISOString().slice(0, 10) : String(date))
      const [y, m, d] = str.slice(0, 10).split('-')
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
      return m && d ? `${months[parseInt(m, 10) - 1] || m} ${parseInt(d, 10)}` : str.slice(5)
    })
    const data = list.map(({ count }) => count)
    return {
      labels: labels.length ? labels : ['No data'],
      datasets: [{
        label: 'Child registrations',
        data: data.length ? data : [0],
        backgroundColor: primary,
        borderColor: primary,
        borderWidth: 1
      }]
    }
  }, [summary.registrationsByDate])

  const applyCustomRange = () => {
    if (customStart && customEnd && customStart <= customEnd) {
      setDateFilter({ type: 'range', startDate: customStart, endDate: customEnd })
      fetchData({ startDate: customStart, endDate: customEnd })
    }
  }

  useEffect(() => {
    document.title = 'Dashboard | RYD Admin'
    fetchData()
  }, [])

  const fetchData = (overrides = {}) => {
    const params = { ...overrides }
    if (!params.days && !params.startDate) {
      if (dateFilter.type === 'range' && dateFilter.startDate && dateFilter.endDate) {
        params.startDate = dateFilter.startDate
        params.endDate = dateFilter.endDate
      } else {
        params.days = dateFilter.days ?? 30
      }
    }
    setLoading(true)
    setError(null)
    axios.get(`${baseUrl}/admin/dashboard`, { params })
      .then((response) => {
        const data = response.data?.data || {}
        setSummary({
          totalParents: data.totalParents ?? 0,
          activeParents: data.activeParents ?? 0,
          totalChildren: data.totalChildren ?? 0,
          totalPrograms: data.totalPrograms ?? 0,
          teacherAssignedCount: data.teacherAssignedCount ?? 0,
          enrolledCount: data.enrolledCount ?? 0,
          totalPackages: data.totalPackages ?? 0,
          totalPartners: data.totalPartners ?? 0,
          registrationsByDate: Array.isArray(data.registrationsByDate) ? data.registrationsByDate : []
        })
      })
      .catch((err) => {
        handleAuthError(err)
        setError(err?.message || 'Failed to load dashboard')
        console.error('Error fetching dashboard:', err)
      })
      .finally(() => setLoading(false))
  }

  const handlePreset = (days) => {
    setDateFilter({ type: 'days', days })
    fetchData({ days })
  }

  const handleExploreCbt = async () => {
    const popup = window.open('about:blank', '_blank')
    try {
      setExploringCbt(true)
      const res = await axios.post(`${baseUrl}/admin/cbt/explore`, {})
      if (res?.data?.status && res?.data?.data?.url) {
        if (popup) {
          try {
            popup.opener = null
          } catch (_) {
            /* ignore */
          }
          popup.location.href = res.data.data.url
        } else {
          openExternalLaunchUrl(res.data.data.url)
        }
        toast.success('Opening CBT...')
        return
      }
      popup?.close()
      toast.error(res?.data?.message || 'Unable to open CBT')
    } catch (e) {
      popup?.close()
      handleAuthError(e)
      toast.error('Unable to open CBT')
    } finally {
      setExploringCbt(false)
    }
  }

  const handleExploreAiTutor = async () => {
    const popup = window.open('about:blank', '_blank')
    try {
      setExploringAi(true)
      const res = await axios.post(`${baseUrl}/admin/ai/explore`, {})
      if (res?.data?.status && res?.data?.data?.url) {
        if (popup) {
          try {
            popup.opener = null
          } catch (_) {
            /* ignore */
          }
          popup.location.href = res.data.data.url
        } else {
          openExternalLaunchUrl(res.data.data.url)
        }
        toast.success('Opening AI Tutor...')
        return
      }
      popup?.close()
      toast.error(res?.data?.message || 'Unable to open AI Tutor')
    } catch (e) {
      popup?.close()
      handleAuthError(e)
      toast.error('Unable to open AI Tutor')
    } finally {
      setExploringAi(false)
    }
  }

  const handleExplorePractice = async () => {
    const popup = window.open('about:blank', '_blank')
    try {
      setExploringPractice(true)
      const res = await axios.post(`${baseUrl}/admin/practice/explore`, {})
      if (res?.data?.status && res?.data?.data?.url) {
        if (popup) {
          try {
            popup.opener = null
          } catch (_) {
            /* ignore */
          }
          popup.location.href = res.data.data.url
        } else {
          openExternalLaunchUrl(res.data.data.url)
        }
        toast.success('Opening Practice...')
        return
      }
      popup?.close()
      toast.error(res?.data?.message || 'Unable to open Practice')
    } catch (e) {
      popup?.close()
      handleAuthError(e)
      toast.error('Unable to open Practice')
    } finally {
      setExploringPractice(false)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Dashboard" />

          {loading && (
            <div className="d-flex align-items-center justify-content-center py-5">
              <Spinner color="primary" />
              <span className="ms-2">Loading dashboard...</span>
            </div>
          )}
          {error && !loading && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between">
              <span>{error}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchData()}>Retry</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Hero strip */}
              <div
                className="rounded-3 p-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
                style={{
                  background: `linear-gradient(135deg, ${primaryLight} 0%, rgba(136, 22, 122, 0.06) 100%)`,
                  border: `1px solid rgba(136, 22, 122, 0.2)`
                }}
              >
                <div className="d-flex align-items-center">
                  <div className="rounded-3 d-flex align-items-center justify-content-center me-3" style={{ width: 56, height: 56, background: 'rgba(136, 22, 122, 0.2)' }}>
                    <Activity size={28} style={{ color: primary }} />
                  </div>
                  <div>
                    <h5 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>Dashboard</h5>
                    <p className="text-muted small mb-0">Overview of platform metrics and key analytics.</p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    color="primary"
                    onClick={handleExploreCbt}
                    disabled={readOnly || exploringCbt || exploringAi || exploringPractice}
                    title={readOnly ? 'Not available for marketer accounts' : undefined}
                  >
                    {exploringCbt ? 'Opening CBT...' : 'Explore CBT'}
                  </Button>
                  <Button
                    color="success"
                    onClick={handleExplorePractice}
                    disabled={readOnly || exploringCbt || exploringAi || exploringPractice}
                    title={readOnly ? 'Not available for marketer accounts' : undefined}
                  >
                    {exploringPractice ? 'Opening Practice...' : 'Explore Practice'}
                  </Button>
                  <Button color="info" onClick={handleExploreAiTutor} disabled={exploringAi || exploringCbt || exploringPractice}>
                    {exploringAi ? 'Opening AI Tutor...' : 'Explore AI Tutor'}
                  </Button>
                </div>
              </div>

              {/* KPI cards */}
              <Row className="g-3 mb-4">
                <Col xl={3} md={6}>
                  <StatCard icon={Users} title="Total Parents" value={summary.totalParents} accent={primary} bgLight={primaryLight} />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard
                    icon={UserCheck}
                    title="Active Parents"
                    value={summary.activeParents}
                    subtitle={`${derived.activeParentRatePct}% of total`}
                    accent={successColor}
                    bgLight={successLight}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard
                    icon={Baby}
                    title="Total Children"
                    value={summary.totalChildren}
                    subtitle={`~${derived.childrenPerParent} per parent`}
                    accent={primary}
                    bgLight={primaryLight}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard icon={BookOpen} title="Total Programs" value={summary.totalPrograms} accent={primary} bgLight={primaryLight} />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard
                    icon={GraduationCap}
                    title="Teacher assigned"
                    value={summary.teacherAssignedCount}
                    accent={successColor}
                    bgLight={successLight}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard
                    icon={TrendingUp}
                    title="Enrolled"
                    value={summary.enrolledCount}
                    accent={primary}
                    bgLight={primaryLight}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard icon={Package} title="Total Packages" value={summary.totalPackages} accent={primary} bgLight={primaryLight} />
                </Col>
                <Col xl={3} md={6}>
                  <StatCard icon={Building2} title="Total Partners" value={summary.totalPartners} accent={primary} bgLight={primaryLight} />
                </Col>
              </Row>

              {/* Child registration chart – full width section with filters */}
              <Card className="border-0 shadow-sm rounded-3 mb-4">
                <CardBody>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                    <h6 className="fw-semibold mb-0 d-flex align-items-center">
                      <UserPlus size={18} className="me-2" style={{ color: primary }} />
                      Child registrations by date
                    </h6>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {PRESETS.map(({ label, days }) => (
                        <Button
                          key={days}
                          size="sm"
                          outline={dateFilter.type !== 'days' || dateFilter.days !== days}
                          color="primary"
                          onClick={() => handlePreset(days)}
                        >
                          {label}
                        </Button>
                      ))}
                      <div className="d-flex align-items-center gap-1">
                        <Input
                          type="date"
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                          className="form-control form-control-sm"
                          style={{ width: '140px' }}
                        />
                        <span className="text-muted">–</span>
                        <Input
                          type="date"
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          className="form-control form-control-sm"
                          style={{ width: '140px' }}
                        />
                        <Button size="sm" color="primary" outline onClick={applyCustomRange}>Apply</Button>
                      </div>
                    </div>
                  </div>
                  <div style={{ height: 320 }}>
                    <Bar
                      data={registrationChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: { mode: 'index' }
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { maxRotation: 45, font: { size: 10 } }
                          },
                          y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(0,0,0,0.06)' },
                            ticks: { stepSize: 1 }
                          }
                        }
                      }}
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Key metrics – below chart, more metrics */}
              <Card className="border-0 shadow-sm rounded-3">
                <CardBody>
                  <h6 className="fw-semibold mb-3 d-flex align-items-center">
                    <TrendingUp size={18} className="me-2" style={{ color: primary }} />
                    Key metrics
                  </h6>
                  <Row>
                    <Col md={6} lg={4}>
                      <MetricBar label="Active parents (of total parents)" value={summary.activeParents} total={summary.totalParents} color={successColor} />
                    </Col>
                    <Col md={6} lg={4}>
                      <MetricBar label="Teacher assignment (programs with teacher)" value={summary.teacherAssignedCount} total={summary.totalPrograms} color={primary} />
                    </Col>
                    <Col md={6} lg={4}>
                      <MetricBar label="Enrollment (enrolled vs programs)" value={summary.enrolledCount} total={summary.totalPrograms} color={primary} />
                    </Col>
                    <Col md={6} lg={4}>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small text-muted">Children per parent</span>
                          <span className="small fw-semibold" style={{ color: primary }}>{derived.childrenPerParent}</span>
                        </div>
                        <div className="rounded-pill overflow-hidden" style={{ height: 8, background: 'rgba(0,0,0,0.06)' }}>
                          <div
                            className="h-100 rounded-pill"
                            style={{
                              width: `${Math.min(100, (parseFloat(derived.childrenPerParent) / 5) * 100)}%`,
                              background: primary,
                              transition: 'width 0.5s ease'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md={6} lg={4}>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small text-muted">Programs without teacher</span>
                          <span className="small fw-semibold" style={{ color: derived.programsWithoutTeacher > 0 ? warnColor : primary }}>{derived.programsWithoutTeacher}</span>
                        </div>
                        <div className="rounded-pill overflow-hidden" style={{ height: 8, background: 'rgba(0,0,0,0.06)' }}>
                          <div
                            className="h-100 rounded-pill"
                            style={{
                              width: summary.totalPrograms ? `${Math.min(100, (derived.programsWithoutTeacher / summary.totalPrograms) * 100)}%` : '0%',
                              background: derived.programsWithoutTeacher > 0 ? warnColor : primary,
                              transition: 'width 0.5s ease'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                  {derived.programsWithoutTeacher > 0 && (
                    <div className="mt-2 p-2 rounded-2 d-flex align-items-center" style={{ background: warnLight }}>
                      <AlertCircle size={16} style={{ color: warnColor }} className="me-2 flex-shrink-0" />
                      <span className="small" style={{ color: '#92400e' }}>
                        {derived.programsWithoutTeacher} program{derived.programsWithoutTeacher !== 1 ? 's' : ''} without teacher assignment
                      </span>
                    </div>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withAuth(Dashboard)
